'use client'

import { useIsLoggedInQuery } from 'auth/hooks'
import * as React from 'react'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { useRouter } from 'next/navigation'
import * as Sentry from '@sentry/nextjs'

export function AuthenticatedOnly({
  children,
  redirectToLogin,
  skeleton,
}: {
  children: React.ReactNode
  redirectToLogin?: boolean
  skeleton?: React.ReactNode
}) {
  const isLoggedInQuery = useIsLoggedInQuery()
  const loggedIn = isLoggedInQuery.data
  const loading = isLoggedInQuery.isPending
  const { push } = useRouter()

  React.useEffect(() => {
    if (!loggedIn && !loading && redirectToLogin) {
      let cancelled = false

      const reportAndRedirect = async () => {
        Sentry.captureMessage('Client-side redirect to login: no active session', {
          level: 'error',
          tags: {
            component: 'auth-guard',
            action: 'redirect-to-login',
          },
        })

        // Ensure the event is delivered before navigating away.
        await Sentry.flush(2000)

        if (!cancelled) {
          push(AppRoutes.LOGIN)
        }
      }

      void reportAndRedirect()

      return () => {
        cancelled = true
      }
    }
  }, [loggedIn, redirectToLogin, push, loading])

  if (!loggedIn) {
    return skeleton ?? null
  }

  return children
}
